import { Link, useLocation } from "react-router-dom";
import { Facebook, Instagram, Youtube, MapPin, Phone, Mail, Clock } from "lucide-react";
import Logo from "./Logo";
import { useSettings, DIRECTIONS_URL, formatAddress } from "@/hooks/SettingsContext";
import { trackDirectionsClick, trackPhoneClick, trackEmailClick } from "@/lib/analytics";
import { LOCATIONS } from "@/data/locations";

const HOME_VERSE = ['"See, I am doing a new thing."', "Isaiah 43:19"];
const LOCATION_VERSE = ['"The Lord your God will be with you wherever you go."', "Joshua 1:9"];
const VERSES = {
  "/": HOME_VERSE,
  "/im-new": ['"Come to me, all you who are weary and burdened, and I will give you rest."', "Matthew 11:28"],
  "/about": ['"We are God\'s handiwork, created in Christ Jesus to do good works."', "Ephesians 2:10"],
  "/vision": ['"Write the vision; make it plain."', "Habakkuk 2:2"],
  "/beliefs": ['"Your word is a lamp for my feet, a light on my path."', "Psalm 119:105"],
  "/kids": ['"Let the little children come to me... the kingdom of heaven belongs to such as these."', "Matthew 19:14"],
  "/sermons": ['"Faith comes by hearing, and hearing by the word of God."', "Romans 10:17"],
  "/events": ['"How good and pleasant it is when God\'s people live together in unity!"', "Psalm 133:1"],
  "/give": ['"God loves a cheerful giver."', "2 Corinthians 9:7"],
  "/pray": ['"The prayer of a righteous person is powerful and effective."', "James 5:16"],
  "/prayer": ['"The prayer of a righteous person is powerful and effective."', "James 5:16"],
  "/churches-in-clay-county-fl": ['"Let us not give up meeting together."', "Hebrews 10:25"],
  "/can-god-forgive-me": ['"If we confess our sins, he is faithful and just to forgive us our sins."', "1 John 1:9"],
  "/anxiety-and-the-bible": ['"Do not be anxious about anything, but in every situation, by prayer... present your requests to God."', "Philippians 4:6"],
  "/how-to-pray": ['"When you pray, go into your room, close the door and pray to your Father."', "Matthew 6:6"],
  "/get-closer-to-god": ['"But as for me, it is good to be near God."', "Psalm 73:28"],
  "/who-is-jesus": ['"I am the way, the truth, and the life."', "John 14:6"],
  "/how-to-follow-jesus": ['"Come, follow me," Jesus said.', "Mark 1:17"],
  "/what-does-it-mean-to-be-saved": ['"Everyone who calls on the name of the Lord will be saved."', "Romans 10:13"],
  "/new-christian": ['"If anyone is in Christ, the new creation has come."', "2 Corinthians 5:17"],
  "/what-is-baptism": ['"We were therefore buried with him through baptism into death... that we too may live a new life."', "Romans 6:4"],
  "/contact": ['"Call to me and I will answer you."', "Jeremiah 33:3"],
  "/lifeline-youth": ['"Don\'t let anyone look down on you because you are young."', "1 Timothy 4:12"],
  "/rally-point": ['"Be strong and courageous... the Lord your God will be with you wherever you go."', "Joshua 1:9"],
  "/in-home": ['"Where two or three gather in my name, there am I with them."', "Matthew 18:20"],
  "/resources": ['"All Scripture is God-breathed and is useful for teaching."', "2 Timothy 3:16"],
  "/resources/what-is-a-christian": ['"To all who received him... he gave the right to become children of God."', "John 1:12"],
  "/resources/how-to-accept-christ": ['"Everyone who calls on the name of the Lord will be saved."', "Romans 10:13"],
  "/resources/why-come-to-church": ['"Let us not give up meeting together."', "Hebrews 10:25"],
  "/resources/baptism-of-the-holy-spirit": ['"You will receive power when the Holy Spirit comes on you."', "Acts 1:8"],
  "/resources/grow-closer-to-god": ['"Draw near to God, and he will draw near to you."', "James 4:8"],
  "/resources/overcoming-worry-and-anxiety": ['"Cast all your anxiety on him because he cares for you."', "1 Peter 5:7"],
  "/resources/overcoming-depression": ['"The Lord is close to the brokenhearted."', "Psalm 34:18"],
};

export default function Footer() {
  const { settings } = useSettings();
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const verse = VERSES[path] || (LOCATIONS[path.slice(1)] || path.startsWith("/church-") ? LOCATION_VERSE : HOME_VERSE);
  const s = settings || {};
  const address = formatAddress(s);
  const hasTimes = s.sunday_time || s.wednesday_time;

  return (
    <footer className="grain relative bg-navy text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 pb-28 pt-16 sm:px-8 sm:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo variant="light" size="footer" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
              Victory Chapel is a new Christian church in Clay County, Florida - a place to encounter Jesus,
              build meaningful relationships, strengthen your family, and discover God's purpose for your life.
            </p>
            <div className="mt-6 flex gap-3">
              {s.facebook && (
                <a href={s.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook"
                   className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-victory hover:bg-victory">
                  <Facebook size={17} />
                </a>
              )}
              {s.instagram && (
                <a href={s.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
                   className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-victory hover:bg-victory">
                  <Instagram size={17} />
                </a>
              )}
              {s.youtube && (
                <a href={s.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-youtube"
                   className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-victory hover:bg-victory">
                  <Youtube size={17} />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <h3 className="font-display text-sm font-700 uppercase tracking-[0.2em] text-white/50">Visit</h3>
            <ul className="mt-5 space-y-4 text-sm text-white/80">
              {hasTimes && (
                <li className="flex gap-3" data-testid="footer-service-times">
                  <Clock size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                  <span>{s.sunday_time}{s.sunday_time && s.wednesday_time ? <br /> : null}{s.wednesday_time}</span>
                </li>
              )}
              <li className="flex gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" onClick={trackDirectionsClick}
                   data-testid="footer-directions" className="transition-colors hover:text-white">
                  {address || "Clay County, Florida"}
                </a>
              </li>
              {s.phone && (
                <li className="flex gap-3">
                  <Phone size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                  <a href={`tel:${s.phone.replace(/[^0-9+]/g, "")}`} onClick={trackPhoneClick} data-testid="footer-phone" className="transition-colors hover:text-white">
                    {s.phone}
                  </a>
                </li>
              )}
              {s.email && (
                <li className="flex gap-3">
                  <Mail size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                  <a href={`mailto:${s.email}`} onClick={trackEmailClick} data-testid="footer-email" className="transition-colors hover:text-white">
                    {s.email}
                  </a>
                </li>
              )}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="font-display text-sm font-700 uppercase tracking-[0.2em] text-white/50">Explore</h3>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[
                ["/im-new", "Plan Your Visit"], ["/about", "About"], ["/vision", "Our Vision"], ["/beliefs", "What We Believe"], ["/kids", "VC Kids"],
                ["/resources", "Free Resources"], ["/churches-in-clay-county-fl", "Find a Church"], ["/sermons", "Sermons"], ["/events", "Events"], ["/give", "Give"],
                ["/lifeline-youth", "Lifeline Youth"], ["/rally-point", "The Rally Point"], ["/in-home", "Clay Groups"],
                ["/prayer", "Prayer"], ["/contact", "Contact"], ["/lake-asbury", "Lake Asbury"],
                ["/middleburg", "Middleburg"], ["/oakleaf", "Oakleaf"], ["/fleming-island", "Fleming Island"],
                ["/orange-park", "Orange Park"], ["/green-cove-springs", "Green Cove Springs"], ["/lakeside", "Lakeside"], ["/keystone-heights", "Keystone Heights"], ["/doctors-inlet", "Doctors Inlet"], ["/privacy", "Privacy Policy"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="inline-block py-2 text-white/80 transition-colors hover:text-white">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center" data-testid="footer-scripture">
          <p className="mx-auto max-w-2xl font-serif text-lg italic leading-snug text-white/80 sm:text-xl">{verse[0]}</p>
          <p className="mt-3 font-display text-[11px] font-700 uppercase tracking-[0.3em] text-rose-300">{verse[1]}</p>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/50 sm:flex-row sm:items-center">
          <p>&copy; {new Date().getFullYear()} Victory Chapel - Clay County, Florida. All rights reserved.</p>
          <p className="font-serif italic text-white/60">"A place for your family, from the very beginning."</p>
        </div>
      </div>
    </footer>
  );
}
